import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, FileText, MessageCircle, Beaker, Leaf, ShieldCheck, Truck,
  HeartPulse, Dumbbell, Sparkles, Sun, Star, Award, Microscope, ClipboardList,
  Package, Mail, Lock, BadgeCheck,
} from "lucide-react";
import HeroVisual from "../components/HeroVisual";
import ProductCard from "../components/ProductCard";
import Testimonial from "../components/Testimonial";
import SEO from "../components/SEO";
import api from "../lib/api";

const WHATS = "https://wa.me/5519992923017?text=Ol%C3%A1%20Biofarmax!%20Quero%20fazer%20meu%20or%C3%A7amento";

const stats = [
  { value: "+500.000", label: "Fórmulas manipuladas", Icon: Beaker },
  { value: "+40.000", label: "Clientes satisfeitos", Icon: HeartPulse },
  { value: "29 anos", label: "De excelência", Icon: Award },
  { value: "4.9 ★", label: "Avaliação média", Icon: Star },
];

const categoryMeta = {
  biobeauty:    { label: "Biobeauty", Icon: Sparkles,   desc: "Dermocosméticos premium · séruns, gloss e tratamentos.", premium: true },
  saude:        { label: "Saúde",     Icon: HeartPulse, desc: "Vitaminas, fitoterápicos e cápsulas premium." },
  fitness:      { label: "Fitness",   Icon: Dumbbell,   desc: "Creatina, energéticos e performance." },
  "bem-estar":  { label: "Bem-Estar", Icon: Sparkles,   desc: "Equilíbrio diário do corpo e da mente." },
  pele:         { label: "Pele",      Icon: Sun,        desc: "Dermocosméticos e proteção solar." },
};

const processSteps = [
  { n: "01", title: "Envie sua receita", text: "Pelo WhatsApp ou diretamente no nosso site, com total privacidade.", Icon: FileText },
  { n: "02", title: "Análise farmacêutica", text: "Nossa equipe técnica valida prescrição, matérias-primas e doses.", Icon: ClipboardList },
  { n: "03", title: "Manipulação premium", text: "Produzimos sua fórmula em laboratório certificado, com rastreabilidade total.", Icon: Microscope },
  { n: "04", title: "Entrega rápida",  text: "Receba em casa, com frete rápido e atendimento humano por todo o Brasil.", Icon: Package },
];

const testimonials = [
  { quote: "Manipulação impecável. Recebi a fórmula em 48h e o atendimento da farmacêutica foi um diferencial enorme.", name: "Camila Andrade", role: "Cliente — Campinas/SP", rating: 5 },
  { quote: "Uso há mais de 2 anos. A qualidade dos colágenos e suplementos é claramente superior. Sinto a diferença.", name: "Renato Lima",    role: "Cliente — São Paulo/SP", rating: 5 },
  { quote: "Atendimento humano, preço justo e produtos de altíssimo padrão. Recomendo de olhos fechados.",                name: "Juliana Mendes", role: "Cliente — Valinhos/SP",  rating: 5 },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/products?featured=true&limit=14").then((r) => setProducts(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        path="/"
        title="Farmácia de Manipulação Premium em Campinas"
        description="Biofarmax · Mais de 500.000 fórmulas manipuladas com precisão farmacêutica. Cápsulas, colágenos, dermocosméticos Biobeauty e suplementos com entrega para todo o Brasil."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Biofarmax · Farmácia de Manipulação em Campinas",
          "url": "https://biofarmax.com.br/",
          "isPartOf": { "@id": "https://biofarmax.com.br/#website" },
          "about": { "@id": "https://biofarmax.com.br/#organization" },
          "description": "Farmácia de manipulação Biofarmax — fórmulas exclusivas em cápsulas, dermocosméticos, colágenos e suplementos. Atendimento farmacêutico personalizado em Campinas/SP.",
          "inLanguage": "pt-BR"
        }}
      />
      {/* ─── HERO ─────────────────────────────────────────────────── */}
      <section className="relative pt-20 sm:pt-24 pb-16 lg:pt-28 lg:pb-24 overflow-hidden gradient-soft grain">
        {/* ambient glow */}
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-40 pointer-events-none"
             style={{ background: "radial-gradient(circle, #C9B47A 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-30 pointer-events-none"
             style={{ background: "radial-gradient(circle, #0F0F0F 0%, transparent 60%)" }} />

        <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center">
          {/* LEFT: TEXT */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10"
          >
            <div className="eyebrow mb-6" data-testid="hero-eyebrow">
              Farmácia · Manipulação · Campinas
            </div>
            <h1 className="h-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] mb-6">
              Sua saúde<br />
              <span className="italic text-gradient-amber">manipulada com precisão</span><br />
              farmacêutica.
            </h1>
            <p className="text-lg text-ink-secondary max-w-xl mb-10 leading-relaxed">
              Fórmulas personalizadas, qualidade premium e acompanhamento especializado. Mais de 500.000 manipulações realizadas com rigor científico e cuidado humano.
            </p>

            <div className="flex flex-wrap gap-3 mb-12">
              <a
                href={WHATS}
                target="_blank"
                rel="noreferrer"
                className="btn-primary"
                data-testid="hero-cta-recipe"
              >
                <FileText size={18} /> Enviar Receita
              </a>
              <a
                href={WHATS}
                target="_blank"
                rel="noreferrer"
                className="btn-whatsapp"
                data-testid="hero-cta-whatsapp"
              >
                <MessageCircle size={18} /> Falar no WhatsApp
              </a>
            </div>

            {/* Trust strip */}
            <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-ink-secondary">
              <span className="inline-flex items-center gap-2"><BadgeCheck size={16} className="text-brand-accent" strokeWidth={2}/> CRF-SP licenciada</span>
              <span className="inline-flex items-center gap-2"><Lock size={16} className="text-brand-accent" strokeWidth={2}/> Pagamento seguro</span>
              <span className="inline-flex items-center gap-2"><Truck size={16} className="text-brand-accent" strokeWidth={2}/> Frete para todo Brasil</span>
            </div>
          </motion.div>

          {/* RIGHT: IMAGE */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-[5/6] lg:aspect-[4/5] max-w-md lg:max-w-none mx-auto w-full"
          >
            <HeroVisual />
          </motion.div>
        </div>
      </section>

      {/* ─── STATS STRIP ──────────────────────────────────────────── */}
      <section className="border-y border-brand-primary/10 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-brand-primary/10">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="px-5 py-8 md:py-10 flex flex-col items-start"
              data-testid={`stat-${i}`}
            >
              <s.Icon size={20} className="text-brand-accent mb-3" strokeWidth={1.6} />
              <div className="font-heading text-3xl md:text-4xl text-brand-primary leading-none">{s.value}</div>
              <div className="text-xs text-ink-secondary mt-2 uppercase tracking-wider font-tech">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── MARQUEE ──────────────────────────────────────────────── */}
      <section className="border-b border-brand-primary/10 py-5 overflow-hidden bg-bg-secondary">
        <div className="marquee-track flex gap-12 whitespace-nowrap text-brand-primary/70">
          {Array(2).fill(0).map((_, k) => (
            <div key={k} className="flex gap-12">
              {["Receita Médica", "PIX com 5% OFF", "Atendimento Humano", "Manipulação Premium", "Entrega Nacional", "Farmacêuticos Habilitados", "Matéria-Prima Selecionada"].map((t, i) => (
                <span key={i} className="font-heading italic text-2xl flex items-center gap-12">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-accent" />
                  {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ─── CATEGORIES ───────────────────────────────────────────── */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-4">
            <div>
              <div className="eyebrow mb-3">Coleções</div>
              <h2 className="h-section text-4xl sm:text-5xl lg:text-6xl">Navegue por categoria</h2>
            </div>
            <Link to="/loja" className="text-brand-primary hover:text-brand-accent font-tech inline-flex items-center gap-2" data-testid="all-products-link">
              Ver tudo <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {cats.filter((c) => categoryMeta[c.slug]).map((c, i) => {
              const meta = categoryMeta[c.slug];
              const Icon = meta.Icon;
              const isPremium = meta.premium;
              return (
                <motion.div
                  key={c.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className={isPremium ? "col-span-2 sm:col-span-2 lg:col-span-1" : ""}
                >
                  <Link
                    to={`/categoria/${c.slug}`}
                    className={`block relative overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] min-h-[200px] sm:min-h-[420px] lg:min-h-[460px] group p-4 sm:p-6 border transition-all duration-500 hover:-translate-y-1 ${
                      isPremium
                        ? "border-brand-accent/40 hover:border-brand-accent hover:shadow-[0_40px_80px_-30px_rgba(201,180,122,0.45)] text-white"
                        : "border-brand-primary/8 hover:border-brand-accent/40 hover:shadow-[0_40px_80px_-30px_rgba(15,15,15,0.22)]"
                    }`}
                    style={{
                      background: isPremium
                        ? "radial-gradient(120% 70% at 50% 0%, rgba(201,180,122,0.45) 0%, transparent 60%), linear-gradient(180deg, #1A1A1A 0%, #0A0A0A 100%)"
                        : "radial-gradient(120% 70% at 50% 0%, rgba(201,180,122,0.10) 0%, transparent 50%), linear-gradient(180deg, #FFFFFF 0%, #F5F2E8 100%)",
                    }}
                    data-testid={`category-card-${c.slug}`}
                  >
                    <div className="absolute inset-0 opacity-30 pointer-events-none">
                      <div className={`absolute top-6 right-6 w-20 h-20 rounded-full border ${isPremium ? "border-brand-accent/60" : "border-brand-accent/30"}`} />
                      <div className={`absolute bottom-10 left-6 w-12 h-12 rounded-full border ${isPremium ? "border-white/20" : "border-brand-primary/15"}`} />
                    </div>
                    {isPremium && (
                      <span className="absolute top-5 left-5 badge-pill bg-brand-accent text-white z-10">
                        <Sparkles size={11} strokeWidth={2} /> Novo · Premium
                      </span>
                    )}
                    <div className="relative flex flex-col h-full min-h-[168px] sm:min-h-[388px] lg:min-h-[428px]">
                      <div className="flex-1 grid place-items-center py-2 sm:py-4">
                        <div className={`w-16 h-16 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full grid place-items-center transition-all duration-500 group-hover:scale-110 ${
                          isPremium
                            ? "bg-brand-accent text-white shadow-[0_30px_60px_-20px_rgba(201,180,122,0.6)]"
                            : "bg-white text-brand-primary shadow-[0_30px_60px_-20px_rgba(15,15,15,0.25)] group-hover:bg-brand-accent"
                        }`}>
                          <Icon className={`w-7 h-7 sm:w-12 sm:h-12 lg:w-[52px] lg:h-[52px] ${isPremium ? "text-white" : "text-brand-primary group-hover:text-white transition"}`} strokeWidth={1.2} />
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <div className={`text-[9px] sm:text-[10px] uppercase tracking-widest font-tech mb-1 ${isPremium ? "text-brand-accent" : "text-ink-secondary"}`}>
                          {c.count} produtos
                        </div>
                        <h3 className="font-heading text-xl sm:text-3xl mb-1 sm:mb-2 leading-tight">{meta.label}</h3>
                        <p className={`text-[11px] sm:text-sm leading-snug line-clamp-2 sm:line-clamp-none ${isPremium ? "text-white/70" : "text-ink-secondary"}`}>{meta.desc}</p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FEATURED PRODUCTS ────────────────────────────────────── */}
      <section className="py-24 lg:py-32 bg-bg-secondary">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-4">
            <div>
              <div className="eyebrow mb-3">Em destaque</div>
              <h2 className="h-section text-4xl sm:text-5xl lg:text-6xl">Os mais procurados</h2>
            </div>
            <p className="max-w-md text-ink-secondary">
              Cápsulas, colágenos e dermocosméticos selecionados pela nossa equipe farmacêutica.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {products.map((p, i) => (
              <div
                key={p.id}
                className={i >= 6 && !showAll ? "hidden md:block" : ""}
              >
                <ProductCard product={p} index={i} />
              </div>
            ))}
          </div>
          {products.length > 6 && !showAll && (
            <div className="md:hidden text-center mt-8">
              <button
                onClick={() => setShowAll(true)}
                className="btn-outline w-full sm:w-auto"
                data-testid="featured-show-more"
              >
                Ver mais {products.length - 6} produtos <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ─── BIOBEAUTY SPOTLIGHT ──────────────────────────────────── */}
      <section className="relative py-24 lg:py-32 overflow-hidden bg-brand-dark text-white">
        <div className="absolute inset-0 opacity-25 pointer-events-none"
             style={{ backgroundImage: "url(/products/biobeauty-CAPA-PDRN.png)", backgroundSize: "cover", backgroundPosition: "right center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]/40 pointer-events-none" />
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-30 pointer-events-none"
             style={{ background: "radial-gradient(circle, #C9B47A 0%, transparent 60%)" }} />

        <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="eyebrow !text-brand-accent mb-4">Nova linha · Premium</div>
            <h2 className="h-section text-4xl sm:text-5xl lg:text-7xl mb-6 leading-[0.95]">
              <span className="italic text-brand-accent">Biobeauty</span><br/>
              dermocosméticos que transformam.
            </h2>
            <p className="text-white/75 text-lg mb-10 max-w-lg leading-relaxed">
              Séruns, gloss, hidratantes e tratamentos avançados desenvolvidos com tecnologia de ponta — peptídeos, ácido hialurônico tri-molecular, Renovage® e ativos botox-like. Resultados visíveis, fórmula assinada Biofarmax.
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              <Link to="/categoria/biobeauty" className="btn-accent" data-testid="biobeauty-cta-shop">
                Explorar a coleção <ArrowRight size={16} />
              </Link>
              <a href={WHATS} target="_blank" rel="noreferrer" className="btn-ghost-light" data-testid="biobeauty-cta-whatsapp">
                <MessageCircle size={16} /> Consultoria de skincare
              </a>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/55 font-tech uppercase tracking-wider">
              <span>· Sérum Botulim</span>
              <span>· Magic Serum</span>
              <span>· Creme Lifting</span>
              <span>· Renovage</span>
              <span>· Gloss</span>
            </div>
          </div>

          {/* Product collage */}
          <div className="grid grid-cols-2 gap-4 relative">
            {[
              { img: "/products/biobeauty-BOTULIM.png",         label: "Sérum Botulim",        price: "R$ 169" },
              { img: "/products/biobeauty-CREME-LIFTING.png",   label: "Creme Lifting",        price: "R$ 69" },
              { img: "/products/biobeauty-MAGIC-SERUM.png",     label: "Magic Serum",          price: "R$ 69" },
              { img: "/products/biobeauty-SERUM-RENOVAGE.png",  label: "Serum Renovage",       price: "R$ 89" },
            ].map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`relative rounded-3xl bg-white/5 backdrop-blur-md border border-white/10 p-4 group hover:border-brand-accent/50 transition-all ${i % 2 === 0 ? "mt-6" : ""}`}
                data-testid={`biobeauty-spotlight-${i}`}
              >
                <div className="aspect-square rounded-2xl overflow-hidden mb-3"
                     style={{ background: "radial-gradient(120% 90% at 50% 110%, rgba(201,180,122,0.18) 0%, transparent 55%), linear-gradient(180deg, #F7F4EB 0%, #E5DFCA 100%)" }}>
                  <img src={p.img} alt={p.label} className="w-full h-full object-contain p-3 group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="text-sm font-heading">{p.label}</div>
                <div className="text-xs text-brand-accent font-tech mt-0.5">{p.price}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─────────────────────────────────────────── */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{ backgroundImage: "url(/generated/molecular-pattern.png)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <div className="eyebrow justify-center mb-3">Como funciona</div>
            <h2 className="h-section text-4xl sm:text-5xl lg:text-6xl mb-5">
              Da receita ao seu <span className="italic text-brand-accent">cuidado</span>
            </h2>
            <p className="text-ink-secondary">
              Um processo transparente, científico e humano em 4 passos simples — feito para que você receba a fórmula certa, com a qualidade que merece.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 relative">
            {/* connecting line */}
            <div className="hidden lg:block absolute top-12 left-[12.5%] right-[12.5%] h-px border-t border-dashed border-brand-accent/30 pointer-events-none" />

            {processSteps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative p-7 bg-white rounded-3xl border border-brand-primary/10 hover:border-brand-accent/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_40px_80px_-30px_rgba(15,15,15,0.18)]"
                data-testid={`process-${i}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-brand-primary text-white grid place-items-center mb-5 shadow-[0_10px_24px_-10px_rgba(15,15,15,0.5)]">
                  <s.Icon size={22} strokeWidth={1.5} />
                </div>
                <div className="font-tech text-xs tracking-[0.3em] text-brand-accent mb-2">{s.n}</div>
                <h3 className="font-heading text-2xl mb-2">{s.title}</h3>
                <p className="text-sm text-ink-secondary leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SOCIAL PROOF / TESTIMONIALS ──────────────────────────── */}
      <section className="py-24 lg:py-32 bg-bg-secondary relative overflow-hidden">
        <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full blur-3xl opacity-30 pointer-events-none"
             style={{ background: "radial-gradient(circle, #C9B47A 0%, transparent 65%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6">
            <div>
              <div className="eyebrow mb-3">Quem confia</div>
              <h2 className="h-section text-4xl sm:text-5xl lg:text-6xl max-w-2xl">
                Histórias reais, <span className="italic text-brand-accent">resultados reais</span>.
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="star-row">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={22} fill="#C9B47A" strokeWidth={1.2} />
                ))}
              </div>
              <div>
                <div className="font-heading text-2xl text-brand-primary leading-none">4.9 / 5.0</div>
                <div className="text-xs text-ink-secondary font-tech uppercase tracking-wider mt-1">+40.000 avaliações</div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <Testimonial key={i} {...t} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── PRESCRIPTION CTA ─────────────────────────────────────── */}
      <section className="relative py-24 lg:py-32 gradient-dark text-white overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none"
             style={{ backgroundImage: "url(/generated/lab-interior.jpg)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#0A0A0A]/80 to-[#0A0A0A]/50 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          <div>
            <div className="eyebrow !text-brand-accent mb-4">Manipulação sob medida</div>
            <h2 className="h-section text-4xl sm:text-5xl lg:text-6xl mb-6">
              Envie sua <span className="italic text-brand-accent">receita</span><br/>direto pelo WhatsApp
            </h2>
            <p className="text-white/75 text-lg mb-10 max-w-lg leading-relaxed">
              Nossa equipe farmacêutica analisa sua prescrição em poucos minutos e prepara o manipulado com matérias-primas selecionadas e total rastreabilidade.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href={WHATS} target="_blank" rel="noreferrer" className="btn-whatsapp" data-testid="recipe-whatsapp-cta">
                <MessageCircle size={18} /> Falar com farmacêutico
              </a>
              <Link to="/loja" className="btn-ghost-light" data-testid="recipe-shop-cta">
                Ver catálogo <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { Icon: Beaker, label: "Laboratório próprio" },
              { Icon: Leaf, label: "Fitoterápicos premium" },
              { Icon: ShieldCheck, label: "Qualidade certificada" },
              { Icon: Truck, label: "Entrega rápida" },
              { Icon: FileText, label: "Receita digital" },
              { Icon: HeartPulse, label: "Acompanhamento" },
            ].map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="p-5 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md hover:border-brand-accent/40 hover:bg-white/10 transition-all"
                data-testid={`benefit-${i}`}
              >
                <b.Icon size={22} className="text-brand-accent mb-3" strokeWidth={1.5} />
                <div className="text-sm">{b.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── NEWSLETTER ───────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
          <Mail size={32} className="mx-auto text-brand-accent mb-4" strokeWidth={1.4} />
          <div className="eyebrow justify-center mb-3">Newsletter</div>
          <h2 className="h-section text-3xl sm:text-4xl mb-3">
            Receba <span className="italic text-brand-accent">5% OFF</span> + dicas de bem-estar
          </h2>
          <p className="text-ink-secondary mb-8">
            Cadastre seu e-mail e ganhe um cupom de boas-vindas. Sem spam, só conteúdo de valor.
          </p>
          <form
            className="flex flex-col sm:flex-row items-stretch gap-2 max-w-md mx-auto"
            onSubmit={(e) => { e.preventDefault(); }}
            data-testid="newsletter-form"
          >
            <input
              type="email"
              required
              placeholder="seu@email.com"
              className="flex-1 px-5 py-3.5 rounded-full bg-bg-secondary border border-brand-primary/10 focus:border-brand-primary focus:outline-none font-tech text-sm"
              data-testid="newsletter-email"
            />
            <button type="submit" className="btn-primary" data-testid="newsletter-submit">
              Quero meu cupom <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
